import { ReactNode, useRef } from 'react';

import { cx } from '@libs/classnames';
import { formatPossibleAnonymousName } from '@libs/formatNames';
import { AnimatedNode } from '@ui/components/Animation';
import { BoardColorCircle } from '@ui/components/BoardColorCircle';
import { GameScoreCirce } from '@ui/components/GameScoreCircle/GameScoreCircle';

import { TableTimer } from '@features/game/ui/TableTimer';
import { get2x2TeamPlayerNumber } from '@features/game/utils/get2x2TeamPlayerNumber';
import { mapGameWinner } from '@features/game/utils/mapGameWinner';

import { calculateClock } from './calculateClock';
import { IPanelGame, IScoresheetClocks } from './types';

import styles from './PlayerPanelMobile.module.css';

/*
    ____,-------------------------------,____
    \   |          Компонент            |   /
    /___|-------------------------------|___\
*/

export type Props = {
  position?: 'top' | 'bottom';
  color: 'black' | 'white';
  game: IPanelGame;
  //-
  isFirstMoveTime?: boolean;
  customPlayersTimeMs?: IScoresheetClocks;
  timePressureSec: number;
  fractionsFromSec?: number;
  //-
  networkProblem?: boolean;
  //-
  ratingGain?: number;
};

export function PlayerPanelMobile({
  position = 'top',
  color,
  game,
  ratingGain,
  customPlayersTimeMs,
  timePressureSec = 10,
  fractionsFromSec = 10,
  isFirstMoveTime,
  networkProblem,
}: Props) {
  const winner = mapGameWinner(game.result);
  const result = winner
    ? winner === 'draw'
      ? 'draw'
      : winner === color
        ? 'win'
        : 'defeat'
    : undefined;
  const isWin = result === 'win';

  const player = game[color];
  const name = formatPossibleAnonymousName(player);

  // Для игры 2на2 (по два игрока за один цвет)
  const is2x2Game = game.whiteSecondPlayer && game.blackSecondPlayer;
  const player2 = game[`${color}SecondPlayer`];
  const name2 = player2 && formatPossibleAnonymousName(player2);
  const activePlayerNumber = player2 ? get2x2TeamPlayerNumber(game.moves.length) : null;

  const { leftMs, customTimeMs, paused } = calculateClock(
    game,
    color,
    customPlayersTimeMs,
    isFirstMoveTime,
  );
  const timestampMs = game.clock?.timestampMs;
  const displayTimeMs = customTimeMs || leftMs;

  return (
    <section
      className={cx(styles.player, {
        [styles.isMuted]: networkProblem,
        [styles.is_2x2]: is2x2Game,
        [styles.is_top]: position === 'top',
        [styles.is_bottom]: position === 'bottom',
      })}
    >
      <div className={styles.mainRow}>
        {displayTimeMs !== undefined && timestampMs !== undefined && (
          <div className={styles.timeCol}>
            <div
              className={cx(styles.timeWrapper, {
                [styles.is_first_move_time]: !paused && isFirstMoveTime,
              })}
            >
              <TableTimer
                className={cx(styles.time, {
                  [styles.isGolden]: isWin,
                })}
                preset="big"
                leftMs={displayTimeMs}
                timePressureSec={timePressureSec}
                timestampMs={timestampMs}
                fractionsFromSec={fractionsFromSec}
                paused={paused}
                timeSync
              />
              <AnimatedNode className={styles.gain}>
                {ratingGain && formatDelta(ratingGain)}
              </AnimatedNode>
            </div>
          </div>
        )}
        <div className={styles.nameCol}>
          <p
            className={cx(styles.name, {
              [styles.is_highlighted]: is2x2Game && !paused && activePlayerNumber === 1,
              [styles.isGolden]: isWin,
            })}
            title={name}
          >
            <BoardColorCircle color={color} />
            <span>{name}</span>
          </p>
          <div
            aria-hidden={!networkProblem}
            className={cx(styles.network, { [styles.isHidden]: !networkProblem })}
          >
            Проблемы с сетью
          </div>
          {is2x2Game && (
            <p
              className={cx(styles.name, {
                [styles.is_highlighted]: is2x2Game && !paused && activePlayerNumber === 2,
                [styles.isGolden]: isWin,
              })}
              title={name2}
            >
              <BoardColorCircle color={color} />
              <span>{name2}</span>
            </p>
          )}
          {player.rating && !is2x2Game && (
            <div
              className={cx(styles.rating, {
                [styles.isGolden]: isWin,
                [styles.isHidden]: networkProblem,
              })}
            >
              {player.rating}{' '}
              <span
                className={cx(styles.update, {
                  [styles.isGolden]: isWin,
                  [styles.isTransparent]: !player.ratingChange,
                })}
              >
                <Memory>
                  {player.ratingChange !== undefined && formatDelta(player.ratingChange)}
                </Memory>
              </span>
            </div>
          )}
        </div>
        <div className={cx(styles.scoreCol, { [styles.isHidden]: !result })}>
          <Memory>{result && <GameScoreCirce result={result} ignoreClubColors />}</Memory>
        </div>
      </div>
    </section>
  );
}

/*
    ____,-------------------------------,____
    \   |           Запчасти            |   /
    /___|-------------------------------|___\
*/

type MemoryProps = {
  children: ReactNode;
};

function Memory({ children }: MemoryProps) {
  const hasChildren = children || children === 0;
  const prevValue = useRef(children);
  if (hasChildren) {
    prevValue.current = children;
  }
  return hasChildren ? children : prevValue.current;
}

/*
    ____,-------------------------------,____
    \   |            Утилиты            |   /
    /___|-------------------------------|___\
*/

function formatDelta(d: number | null): string | null {
  if (!d) return null;
  return d < 0 ? d.toString() : `+${d}`;
}
